import { useState, useEffect } from 'react';
import { useSettings } from '../../providers/SettingsContext';

export const PICKUP_LOCATIONS = [
  {
    id: 'costa_urbana',
    name: 'Costa Urbana Shopping',
    city: 'Ciudad de la Costa',
    department: 'Canelones'
  },
  {
    id: 'punta_carretas',
    name: 'Punta Carretas Shopping',
    city: 'Montevideo',
    department: 'Montevideo'
  }
];

export const DEFAULT_SHIPPING_COSTS = {
  dac_montevideo: 290,
  envio_interior_dac: 290,
  envio_interior_domicilio: 360
};

const ShippingSelector = ({ selectedShipping, selectedPickup, onShippingChange, ciudad, pickupError = false }) => {
  const { settings } = useSettings();
  const [selected, setSelected] = useState(selectedShipping || 'retiro_local');
  const [pickupLocationId, setPickupLocationId] = useState(selectedPickup || '');
  const costs = { ...DEFAULT_SHIPPING_COSTS, ...(settings?.shippingCosts || {}) };
  const shippingOptions = {
    dac_montevideo: {
      type: 'dac_montevideo', label: 'Montevideo', description: 'Entrega a domicilio dentro de Montevideo',
      cost: Number(costs.dac_montevideo), estimatedDays: '1-2 días hábiles', icon: 'fa-location-dot'
    },
    retiro_local: {
      type: 'retiro_local', label: 'Retiro en pickup', description: 'Elegí el pickup donde querés retirar tu pedido',
      cost: 0, estimatedDays: 'Disponible en 24hs', icon: 'fa-store'
    },
    envio_interior_dac: {
      type: 'envio_interior_dac', label: 'Interior — Agencia DAC', description: 'Envío para retirar en una agencia DAC del interior',
      cost: Number(costs.envio_interior_dac), estimatedDays: '3-5 días hábiles', icon: 'fa-box'
    },
    envio_interior_domicilio: {
      type: 'envio_interior_domicilio', label: 'Interior — Domicilio', description: 'Entrega a domicilio en el interior',
      cost: Number(costs.envio_interior_domicilio), estimatedDays: '3-5 días hábiles', icon: 'fa-truck'
    }
  };

  useEffect(() => {
    // Si la ciudad es Montevideo, sugerir DAC
    if (ciudad && ciudad.toLowerCase().includes('montevideo') && !selectedShipping) {
      setSelected('dac_montevideo');
      onShippingChange(shippingOptions.dac_montevideo);
    }
  }, [ciudad]);

  useEffect(() => {
    // Notificar al padre cuando cambia la selección
    if (selected && shippingOptions[selected]) {
      const pickupLocation = selected === 'retiro_local'
        ? PICKUP_LOCATIONS.find(location => location.id === pickupLocationId)
        : null;
      onShippingChange({
        ...shippingOptions[selected],
        ...(pickupLocation ? {
          label: `Retiro en pickup · ${pickupLocation.city} · ${pickupLocation.name}`,
          pickupLocation
        } : {})
      });
    }
  }, [selected, pickupLocationId, costs.dac_montevideo, costs.envio_interior_dac, costs.envio_interior_domicilio]);

  const handleSelect = (type) => {
    setSelected(type);
  };

  const handlePickupSelect = (event, locationId) => {
    event.stopPropagation();
    setPickupLocationId(locationId);
  };

  return (
    <div className="shipping-selector">
      
      <div className="shipping-options">
        {Object.entries(shippingOptions).map(([key, option]) => (
          <div 
            key={key}
            className={`shipping-option ${selected === key ? 'selected' : ''}`}
            onClick={() => handleSelect(key)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') handleSelect(key);
            }}
            role="radio"
            aria-checked={selected === key}
            tabIndex={0}
          >
            <div className="shipping-option-content">
              <div className="shipping-icon">
                <i className={`fa-solid ${option.icon}`}></i>
              </div>
              <div className="shipping-details">
                <div className="shipping-label">{option.label}</div>
                <div className="shipping-description">{option.description}</div>
                <div className="shipping-time">
                  <i className="fa-regular fa-clock me-1"></i>
                  {option.estimatedDays}
                </div>
              </div>
              <div className="shipping-price">
                {option.cost === 0 ? (
                  <span className="free-badge">GRATIS</span>
                ) : (
                  <span className="price">${option.cost}</span>
                )}
              </div>
              <div className="shipping-check">
                {selected === key && <i className="fa-solid fa-circle-check"></i>}
              </div>
            </div>
            {key === 'retiro_local' && selected === key && (
              <div className={`pickup-selector ${pickupError && !pickupLocationId ? 'has-error' : ''}`} onClick={(event) => event.stopPropagation()}>
                <div className="pickup-selector-title">Seleccioná dónde retirar <span>*</span></div>
                <div className="pickup-options" role="radiogroup" aria-label="Punto de pickup">
                  {PICKUP_LOCATIONS.map(location => (
                    <button
                      key={location.id}
                      type="button"
                      className={`pickup-option ${pickupLocationId === location.id ? 'selected' : ''}`}
                      onClick={(event) => handlePickupSelect(event, location.id)}
                      aria-pressed={pickupLocationId === location.id}
                    >
                      <i className="fa-solid fa-location-dot"></i>
                      <span>
                        <strong>Retirar en pickup {location.city}</strong>
                        <small>{location.name}</small>
                      </span>
                      {pickupLocationId === location.id && <i className="fa-solid fa-circle-check pickup-option-check"></i>}
                    </button>
                  ))}
                </div>
                {pickupError && !pickupLocationId && <div className="pickup-error">Elegí uno de los dos puntos de pickup.</div>}
              </div>
            )}
          </div>
        ))}
      </div>

      <style jsx="true">{`
        .shipping-selector {
          margin-bottom: 1.5rem;
        }

        .shipping-options {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .shipping-option {
          border: 2px solid #e0e0e0;
          border-radius: 12px;
          padding: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
          background: #fff;
        }

        .shipping-option:hover {
          border-color: #1f8e38;
          background: #f8fff9;
        }

        .shipping-option.selected {
          border-color: #1f8e38;
          background: linear-gradient(135deg, #f0fff4 0%, #e8f5e9 100%);
          box-shadow: 0 2px 8px rgba(31, 142, 56, 0.15);
        }

        .shipping-option-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .shipping-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #1f8e38 0%, #2ecc71 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.25rem;
          flex-shrink: 0;
        }

        .shipping-option.selected .shipping-icon {
          background: linear-gradient(135deg, #155d24 0%, #1f8e38 100%);
        }

        .shipping-details {
          flex: 1;
        }

        .shipping-label {
          font-weight: 600;
          font-size: 1rem;
          color: #333;
          margin-bottom: 0.25rem;
        }

        .shipping-description {
          font-size: 0.85rem;
          color: #666;
          margin-bottom: 0.25rem;
        }

        .shipping-time {
          font-size: 0.8rem;
          color: #888;
        }

        .shipping-price {
          text-align: right;
          min-width: 80px;
          width: 80px;
          flex-shrink: 0;
          display: flex;
          justify-content: flex-end;
          align-items: center;
        }

        .shipping-price .price {
          font-size: 1.25rem;
          font-weight: 700;
          color: #333;
        }

        .shipping-price .free-badge {
          background: linear-gradient(135deg, #1f8e38 0%, #2ecc71 100%);
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.5px;
          white-space: nowrap;
        }

        .shipping-check {
          width: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .shipping-check i {
          color: #1f8e38;
          font-size: 1.5rem;
        }

        .pickup-selector {
          border-top: 1px solid #cbe8d1;
          margin-top: 1rem;
          padding-top: 1rem;
        }

        .pickup-selector.has-error {
          border-top-color: #dc3545;
        }

        .pickup-selector-title {
          color: #2c3e50;
          font-size: 0.9rem;
          font-weight: 700;
          margin-bottom: 0.65rem;
        }

        .pickup-selector-title span,
        .pickup-error {
          color: #dc3545;
        }

        .pickup-options {
          display: grid;
          gap: 0.65rem;
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .pickup-option {
          align-items: center;
          background: #fff;
          border: 1px solid #d7e5da;
          border-radius: 10px;
          color: #344054;
          display: flex;
          gap: 0.7rem;
          min-height: 72px;
          padding: 0.8rem;
          text-align: left;
          transition: all 0.2s ease;
        }

        .pickup-option:hover,
        .pickup-option.selected {
          background: #edf8ef;
          border-color: #1f8e38;
        }

        .pickup-option > i:first-child {
          color: #1f8e38;
          font-size: 1.15rem;
        }

        .pickup-option span {
          display: grid;
          flex: 1;
          gap: 0.2rem;
        }

        .pickup-option small {
          color: #667085;
        }

        .pickup-option-check {
          color: #1f8e38;
        }

        .pickup-error {
          font-size: 0.8rem;
          margin-top: 0.5rem;
        }

        @media (max-width: 576px) {
          .shipping-option-content {
            flex-wrap: wrap;
          }

          .shipping-icon {
            width: 40px;
            height: 40px;
            font-size: 1rem;
          }

          .shipping-details {
            flex: 1 1 calc(100% - 60px);
          }

          .shipping-price {
            margin-left: 56px;
            margin-top: 0.5rem;
          }

          .shipping-check {
            position: absolute;
            top: 1rem;
            right: 1rem;
          }

          .shipping-option {
            position: relative;
          }

          .pickup-options {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default ShippingSelector;
